import { z } from "zod";
import { FIVE_MINUTES, ONE_HOUR, requestedCacheWrites } from "../../shared/cache-writes";
import type { ModelErrorType } from "../../shared/errors";
import {
  argumentsText,
  contentText,
  errorCode,
  finiteNumber,
  isRecord,
  jsonObject,
  jsonObjects,
  nonEmptyString,
  readableText,
  records,
} from "../../shared/json-body";
import { canonicalModelName } from "../../shared/models";
import { type RoutingFacts, skuById, skuIdOf } from "../../shared/pricing";
import { encryptedReasoningPart, toolPart } from "../../shared/prompt";
import type {
  DeclaredTool,
  ModelPrompt,
  ModelReply,
  ModelRequestSettings,
  ModelToolCall,
  ModelTransportDefinition,
  OutputContract,
  PromptPart,
  StreamChunk,
} from "../../shared/transport";
import type { CacheWrite, ModelUsage } from "../../shared/usage";

const ANTHROPIC_VERSION = "2023-06-01";
/** The most output a request may ask for when its SKU names no limit. */
const MAX_OUTPUT_TOKENS = 64_000;

/** Beta features a caller may turn on; none of them keeps state at Anthropic or changes price. */
const allowedBetas = new Set([
  "interleaved-thinking-2025-05-14",
  "fine-grained-tool-streaming-2025-05-14",
  "extended-cache-ttl-2025-04-11",
  "token-efficient-tools-2025-02-19",
]);

const CacheControl = z.strictObject({
  type: z.literal("ephemeral"),
  ttl: z.enum(["5m", "1h"]).optional(),
});
const cacheControl = CacheControl.nullable().optional();

/** Citations are echoed back from earlier responses as Anthropic sent them. */
const Citations = z
  .array(z.looseObject({ type: z.string() }))
  .nullable()
  .optional();

const TextBlock = z.strictObject({
  type: z.literal("text"),
  text: z.string(),
  cache_control: cacheControl,
  citations: Citations,
});

const ImageBlock = z.strictObject({
  type: z.literal("image"),
  source: z.discriminatedUnion("type", [
    z.strictObject({
      type: z.literal("base64"),
      media_type: z.enum(["image/jpeg", "image/png", "image/gif", "image/webp"]),
      data: z.string(),
    }),
    z.strictObject({ type: z.literal("url"), url: z.url() }),
  ]),
  cache_control: cacheControl,
});

const DocumentBlock = z.strictObject({
  type: z.literal("document"),
  source: z.discriminatedUnion("type", [
    z.strictObject({
      type: z.literal("base64"),
      media_type: z.literal("application/pdf"),
      data: z.string(),
    }),
    z.strictObject({
      type: z.literal("text"),
      media_type: z.literal("text/plain"),
      data: z.string(),
    }),
    z.strictObject({
      type: z.literal("content"),
      content: z.union([
        z.string(),
        z.array(z.discriminatedUnion("type", [TextBlock, ImageBlock])),
      ]),
    }),
    z.strictObject({ type: z.literal("url"), url: z.url() }),
  ]),
  title: z.string().nullable().optional(),
  context: z.string().nullable().optional(),
  citations: z.strictObject({ enabled: z.boolean().optional() }).nullable().optional(),
  cache_control: cacheControl,
});

const SearchResultBlock = z.strictObject({
  type: z.literal("search_result"),
  source: z.string(),
  title: z.string(),
  content: z.array(TextBlock),
  citations: z.strictObject({ enabled: z.boolean().optional() }).optional(),
  cache_control: cacheControl,
});

const Caller = z.looseObject({ type: z.string() }).optional();

const ToolResultBlock = z.strictObject({
  type: z.literal("tool_result"),
  tool_use_id: z.string(),
  content: z
    .union([
      z.string(),
      z.array(
        z.discriminatedUnion("type", [TextBlock, ImageBlock, DocumentBlock, SearchResultBlock]),
      ),
    ])
    .optional(),
  is_error: z.boolean().optional(),
  cache_control: cacheControl,
});

/** A server tool's result, echoed back from an earlier response as Anthropic sent it. */
const serverToolResult = <Type extends string>(type: Type) =>
  z.strictObject({
    type: z.literal(type),
    tool_use_id: z.string(),
    content: z.unknown(),
    cache_control: cacheControl,
    caller: Caller,
  });

const ContentBlock = z.discriminatedUnion("type", [
  TextBlock,
  ImageBlock,
  DocumentBlock,
  SearchResultBlock,
  z.strictObject({ type: z.literal("thinking"), thinking: z.string(), signature: z.string() }),
  z.strictObject({ type: z.literal("redacted_thinking"), data: z.string() }),
  z.strictObject({
    type: z.literal("tool_use"),
    id: z.string(),
    name: z.string(),
    input: z.record(z.string(), z.unknown()),
    cache_control: cacheControl,
    caller: Caller,
  }),
  ToolResultBlock,
  z.strictObject({
    type: z.literal("server_tool_use"),
    id: z.string(),
    name: z.enum(["web_search", "web_fetch"]),
    input: z.record(z.string(), z.unknown()),
    cache_control: cacheControl,
    caller: Caller,
  }),
  serverToolResult("web_search_tool_result"),
  serverToolResult("web_fetch_tool_result"),
]);

const Message = z.strictObject({
  role: z.enum(["user", "assistant"]),
  content: z.union([z.string(), z.array(ContentBlock)]),
});

const domains = z.array(z.string()).nullable().optional();

const CustomTool = z.strictObject({
  type: z.literal("custom").nullable().optional(),
  name: z.string().regex(/^[a-zA-Z0-9_-]{1,128}$/),
  description: z.string().optional(),
  input_schema: z.looseObject({ type: z.literal("object") }),
  cache_control: cacheControl,
  strict: z.boolean().optional(),
  defer_loading: z.boolean().optional(),
  eager_input_streaming: z.boolean().nullable().optional(),
  input_examples: z.array(z.record(z.string(), z.unknown())).optional(),
});

const clientTool = <Type extends string, Name extends string>(type: Type, name: Name) =>
  z.strictObject({
    type: z.literal(type),
    name: z.literal(name),
    cache_control: cacheControl,
    defer_loading: z.boolean().optional(),
    strict: z.boolean().optional(),
  });

const webSearchTool = <Type extends string>(type: Type) =>
  z.strictObject({
    type: z.literal(type),
    name: z.literal("web_search"),
    allowed_domains: domains,
    blocked_domains: domains,
    max_uses: z.number().int().positive().nullable().optional(),
    user_location: z
      .strictObject({
        type: z.literal("approximate"),
        city: z.string().nullable().optional(),
        country: z.string().nullable().optional(),
        region: z.string().nullable().optional(),
        timezone: z.string().nullable().optional(),
      })
      .nullable()
      .optional(),
    cache_control: cacheControl,
    defer_loading: z.boolean().optional(),
    strict: z.boolean().optional(),
  });

const webFetchTool = <Type extends string>(type: Type) =>
  z.strictObject({
    type: z.literal(type),
    name: z.literal("web_fetch"),
    allowed_domains: domains,
    blocked_domains: domains,
    citations: z.strictObject({ enabled: z.boolean().optional() }).nullable().optional(),
    max_content_tokens: z.number().int().positive().nullable().optional(),
    max_uses: z.number().int().positive().nullable().optional(),
    cache_control: cacheControl,
    defer_loading: z.boolean().optional(),
    strict: z.boolean().optional(),
  });

/**
 * Tools the caller runs, plus web search and web fetch, which Anthropic runs and Pax prices. Code
 * execution, tool search, MCP and computer use are refused: they keep state in an Anthropic
 * container or have no price.
 */
const Tool = z.union([
  CustomTool,
  z.discriminatedUnion("type", [
    clientTool("bash_20250124", "bash"),
    clientTool("text_editor_20250124", "str_replace_editor"),
    clientTool("text_editor_20250429", "str_replace_based_edit_tool"),
    clientTool("text_editor_20250728", "str_replace_based_edit_tool").extend({
      max_characters: z.number().int().positive().nullable().optional(),
    }),
    clientTool("memory_20250818", "memory"),
    webSearchTool("web_search_20250305"),
    webSearchTool("web_search_20260209"),
    webFetchTool("web_fetch_20250910"),
    webFetchTool("web_fetch_20260209"),
  ]),
]);

const disableParallelToolUse = z.boolean().optional();

const ToolChoice = z.discriminatedUnion("type", [
  z.strictObject({ type: z.literal("auto"), disable_parallel_tool_use: disableParallelToolUse }),
  z.strictObject({ type: z.literal("any"), disable_parallel_tool_use: disableParallelToolUse }),
  z.strictObject({
    type: z.literal("tool"),
    name: z.string(),
    disable_parallel_tool_use: disableParallelToolUse,
  }),
  z.strictObject({ type: z.literal("none") }),
]);

const thinkingDisplay = z.enum(["summarized", "omitted"]).nullable().optional();

const Thinking = z.discriminatedUnion("type", [
  z.strictObject({
    type: z.literal("enabled"),
    budget_tokens: z.number().int().min(1024),
    display: thinkingDisplay,
  }),
  z.strictObject({ type: z.literal("adaptive"), display: thinkingDisplay }),
  z.strictObject({ type: z.literal("disabled") }),
]);

export const AnthropicMessagesRequest = z
  .strictObject({
    model: z.string().min(1).meta({ example: "claude-opus-5-5" }),
    max_tokens: z.number().int().positive().meta({
      description: "Required. At most the model's own limit.",
      example: 8192,
    }),
    messages: z.array(Message),
    system: z.union([z.string(), z.array(TextBlock)]).optional(),
    stream: z.boolean().optional(),
    stop_sequences: z.array(z.string()).optional(),
    temperature: z.number().min(0).max(1).optional(),
    top_k: z.number().int().nonnegative().optional(),
    top_p: z.number().min(0).max(1).optional(),
    tools: z.array(Tool).optional(),
    tool_choice: ToolChoice.optional(),
    thinking: Thinking.optional(),
    output_config: z
      .strictObject({
        effort: z.enum(["low", "medium", "high", "xhigh", "max"]).nullable().optional(),
        format: z
          .strictObject({
            type: z.literal("json_schema"),
            schema: z.record(z.string(), z.unknown()),
          })
          .nullable()
          .optional(),
      })
      .optional(),
    cache_control: cacheControl,
    metadata: z.strictObject({ user_id: z.string().max(256).nullable().optional() }).optional(),
    service_tier: z.enum(["auto", "standard_only"]).optional(),
    inference_geo: z.enum(["global", "us"]).nullable().optional(),
  })
  .meta({
    id: "AnthropicMessagesRequest",
    description:
      "An Anthropic Messages request. Every field Anthropic documents is accepted except `container`, `diagnostics` and files: those keep state at Anthropic. Any other field is refused.",
    example: {
      model: "claude-opus-5-5",
      max_tokens: 8192,
      messages: [{ role: "user", content: "Who holds Burgundy in 1444?" }],
    },
  });
export type AnthropicMessagesRequest = z.infer<typeof AnthropicMessagesRequest>;

export const AnthropicMessage = z
  .looseObject({
    id: z.string(),
    type: z.literal("message"),
    model: z.string(),
    usage: z.looseObject({}),
  })
  .meta({
    id: "AnthropicMessage",
    description:
      "Anthropic's response, byte for byte: a message, or its server-sent events when `stream` is true.",
  });

/** Anthropic's Messages API, called directly and priced from Anthropic's published rates. */
export const anthropicMessages: ModelTransportDefinition<AnthropicMessagesRequest> = {
  vendor: "anthropic",
  provider: "anthropic",
  acceptsPath: path => path === "v1/messages",
  upstreamUrl: () => "https://api.anthropic.com/v1/messages",
  credential: "ANTHROPIC_API_KEY",
  authorize: (headers, credential) => {
    headers.set("x-api-key", credential);
    if (!headers.has("anthropic-version")) headers.set("anthropic-version", ANTHROPIC_VERSION);
  },
  request: AnthropicMessagesRequest,
  headers: {
    "content-type": z.string().regex(/^application\/json\b/),
    accept: z.string(),
    "anthropic-version": z.literal(ANTHROPIC_VERSION),
    "anthropic-beta": z
      .string()
      .refine(
        value => value.split(",").every(beta => allowedBetas.has(beta.trim())),
        `Pax allows only these betas: ${[...allowedBetas].join(", ")}.`,
      ),
  },
  query: {},
  billedBy: "anthropic",
  readRequestedFacts: request => {
    const body = jsonObject(request.body);
    return anthropicRequestedFacts(nonEmptyString(body.model), nonEmptyString(body.inference_geo));
  },
  skuOf: anthropicSkuOf,
  refusal: (_path, request) => {
    const sku = skuById(
      anthropicSkuOf(anthropicRequestedFacts(request.model, request.inference_geo ?? undefined)),
    );
    if (!sku)
      return { refusal: "model_not_found", message: `Pax has no price for ${request.model}.` };
    const limit = sku.maxOutputTokens ?? MAX_OUTPUT_TOKENS;
    if (request.max_tokens > limit)
      return {
        refusal: "invalid_request",
        message: `max_tokens is ${request.max_tokens}, above ${request.model}'s limit of ${limit}.`,
      };
    return null;
  },
  readModel: (_path, requestBody) => nonEmptyString(jsonObject(requestBody).model),
  readUsage,
  readSettings: (_path, requestBody) => readSettings(requestBody),
  readPrompt,
  readReply,
  readChunks,
  readOutputContract: (_path, requestBody) => readOutputContract(requestBody),
  errorTypeOf: code => (code ? anthropicErrorTypes[code] : undefined),
};

const anthropicErrorTypes: Record<string, ModelErrorType> = {
  overloaded_error: "overloaded",
  api_error: "server_error",
  timeout_error: "timeout",
  rate_limit_error: "rate_limited",
  authentication_error: "auth",
  permission_error: "auth",
  billing_error: "billing",
  not_found_error: "model_not_found",
  request_too_large: "invalid_request",
  invalid_request_error: "invalid_request",
};

/** Client tools carry an input schema; server tools such as web search are Anthropic's own. */
function readOutputContract(requestBody: string): OutputContract {
  const body = jsonObject(requestBody);
  const output = isRecord(body.output_config) ? body.output_config : {};
  const format = isRecord(output.format) ? output.format : {};
  const tools = new Map<string, DeclaredTool>();
  for (const tool of records(body.tools)) {
    const name = nonEmptyString(tool.name);
    if (name) tools.set(name, { json: true, schema: tool.input_schema });
  }
  return {
    tools,
    openTools: false,
    json: format.type === "json_schema",
    responseSchema: format.type === "json_schema" ? format.schema : undefined,
  };
}

/**
 * A response's usage, merged across the `message_start` and `message_delta` events of a stream.
 * Anthropic's `input_tokens` leaves out cache reads and writes, which are added back here.
 * Anthropic takes no audio.
 */
function readUsage(body: string, requestBody: string): ModelUsage | undefined {
  let model: string | undefined;
  const usage: Record<string, unknown> = {};
  for (const event of jsonObjects(body)) {
    const message = isRecord(event.message) ? event.message : event;
    model = nonEmptyString(message.model) ?? model;
    for (const source of [message.usage, event.usage])
      if (isRecord(source)) Object.assign(usage, definedFields(source));
  }
  const uncached = finiteNumber(usage.input_tokens);
  const outputTokens = finiteNumber(usage.output_tokens);
  if (uncached === undefined && outputTokens === undefined) return;
  const read = finiteNumber(usage.cache_read_input_tokens) ?? 0;
  const cacheWrites = readCacheWrites(usage, requestBody);
  const written = cacheWrites.reduce((sum, write) => sum + write.tokens, 0);
  return {
    model,
    inputTokens: (uncached ?? 0) + read + written,
    audioInputTokens: 0,
    implicitCacheReadTokens: 0,
    explicitCacheReadTokens: read,
    cacheWrites,
    outputTokens: outputTokens ?? 0,
    reasoningTokens: 0,
    toolUses: readToolUses(usage),
    serviceTier: usage.speed === "fast" ? "fast" : nonEmptyString(usage.service_tier),
    region: nonEmptyString(usage.inference_geo),
  };
}

/** `server_tool_use` counts each server tool's uses as `<tool>_requests`. */
function readToolUses(usage: Record<string, unknown>): Record<string, number> {
  const counts = isRecord(usage.server_tool_use) ? usage.server_tool_use : {};
  const toolUses: Record<string, number> = {};
  for (const [key, value] of Object.entries(counts)) {
    const uses = finiteNumber(value);
    if (key.endsWith("_requests") && uses) toolUses[key.slice(0, -"_requests".length)] = uses;
  }
  return toolUses;
}

const definedFields = (source: Record<string, unknown>) =>
  Object.fromEntries(Object.entries(source).filter(([, value]) => value != null));

/** Writes by lifetime when the response breaks them down, otherwise as the request asked for them. */
function readCacheWrites(usage: Record<string, unknown>, requestBody: string): CacheWrite[] {
  const byLifetime = isRecord(usage.cache_creation) ? usage.cache_creation : undefined;
  if (byLifetime) {
    return [
      { tokens: finiteNumber(byLifetime.ephemeral_5m_input_tokens) ?? 0, ttlSeconds: FIVE_MINUTES },
      { tokens: finiteNumber(byLifetime.ephemeral_1h_input_tokens) ?? 0, ttlSeconds: ONE_HOUR },
    ].filter(write => write.tokens > 0);
  }
  return requestedCacheWrites(finiteNumber(usage.cache_creation_input_tokens) ?? 0, requestBody);
}

function readSettings(requestBody: string): ModelRequestSettings {
  const body = jsonObject(requestBody);
  const thinking = isRecord(body.thinking) ? body.thinking : {};
  const output = isRecord(body.output_config) ? body.output_config : {};
  const format = isRecord(output.format) ? output.format : {};
  return {
    model: nonEmptyString(body.model),
    maxTokens: finiteNumber(body.max_tokens),
    temperature: finiteNumber(body.temperature),
    topP: finiteNumber(body.top_p),
    stream: body.stream === true,
    reasoningLevel: nonEmptyString(output.effort) ?? nonEmptyString(thinking.type),
    outputType: format.type === "json_schema" ? "json" : undefined,
  };
}

/** Tools, then the system prompt, then the messages: the order Anthropic caches prompts in. */
function readPrompt(requestBody: string): ModelPrompt {
  const body = jsonObject(requestBody);
  const tools = records(body.tools).map(toolPart);
  const system = typeof body.system === "string" ? [{ text: body.system }] : records(body.system);
  const conversation: PromptPart[] = [];
  const toolResults = new Map<string, string>();
  for (const message of records(body.messages)) {
    const blocks =
      typeof message.content === "string"
        ? [{ type: "text", text: message.content }]
        : records(message.content);
    const assistant = message.role === "assistant";
    for (const block of blocks) {
      const part = blockPart(block, assistant, toolResults);
      if (part) conversation.push(part);
    }
  }
  return {
    parts: [
      ...tools,
      ...system.map(block => {
        const text = nonEmptyString(block.text) ?? "";
        return { role: "system" as const, label: "System", text, sent: text };
      }),
      ...conversation,
    ],
    toolResults,
  };
}

function blockPart(
  block: Record<string, unknown>,
  assistant: boolean,
  toolResults: Map<string, string>,
): PromptPart | undefined {
  const type = nonEmptyString(block.type) ?? "text";
  if (type === "text") {
    const text = nonEmptyString(block.text) ?? "";
    return assistant
      ? { role: "assistant", label: "Assistant", text, sent: text }
      : { role: "user", label: "User", text, sent: text };
  }
  if (type === "thinking") {
    const thinking = nonEmptyString(block.thinking);
    return thinking
      ? { role: "assistant", label: "Reasoning", text: thinking, sent: thinking }
      : encryptedReasoningPart(nonEmptyString(block.signature) ?? "");
  }
  if (type === "redacted_thinking") return encryptedReasoningPart(argumentsText(block.data));
  if (type === "tool_use" || type === "server_tool_use") {
    return {
      role: "assistant",
      label: `Calls ${nonEmptyString(block.name) ?? "a tool"}`,
      text: readableText(block.input),
      sent: argumentsText(block.input),
    };
  }
  if (type === "tool_result" || type.endsWith("_tool_result")) {
    const output = typeof block.content === "string" ? block.content : contentText(block.content);
    const id = nonEmptyString(block.tool_use_id);
    if (id) toolResults.set(id, output);
    return { role: "tool", label: "Tool result", text: readableText(output), sent: output };
  }
  return {
    role: assistant ? "assistant" : "user",
    label: type,
    text: JSON.stringify(block, null, 2),
    sent: JSON.stringify(block),
  };
}

/** Reads a whole message, or rebuilds it from the content blocks a stream sent. */
function readReply(responseBody: string): ModelReply {
  const reply: ModelReply = {
    reasoning: "",
    reasoningEncryptedWithoutSummary: false,
    text: "",
    toolCalls: [],
    finishReasons: [],
    nativeFinishReasons: [],
  };
  const blocks = new Map<number, Record<string, unknown>>();
  const toolInputs = new Map<number, string>();
  let stopReason: string | undefined;
  for (const event of jsonObjects(responseBody)) {
    const message = isRecord(event.message) ? event.message : event;
    if (message.type === "message" || event.type === "message_start") {
      reply.id = nonEmptyString(message.id) ?? reply.id;
      reply.model = nonEmptyString(message.model) ?? reply.model;
      for (const [index, block] of records(message.content).entries()) blocks.set(index, block);
    }
    if (isRecord(event.error)) {
      reply.errorType = errorCode(event.error);
      reply.errorMessage = nonEmptyString(event.error.message);
    }
    const usage = isRecord(event.usage)
      ? event.usage
      : isRecord(message.usage)
        ? message.usage
        : {};
    reply.serviceTier =
      (usage.speed === "fast" ? "fast" : nonEmptyString(usage.service_tier)) ?? reply.serviceTier;
    reply.region = nonEmptyString(usage.inference_geo) ?? reply.region;
    const delta = isRecord(event.delta) ? event.delta : {};
    stopReason =
      nonEmptyString(message.stop_reason) ?? nonEmptyString(delta.stop_reason) ?? stopReason;
    const index = finiteNumber(event.index);
    if (index === undefined) continue;
    if (event.type === "content_block_start" && isRecord(event.content_block))
      blocks.set(index, { ...event.content_block });
    if (event.type !== "content_block_delta") continue;
    const block = blocks.get(index) ?? {};
    blocks.set(index, block);
    if (delta.type === "text_delta") block.text = `${block.text ?? ""}${delta.text ?? ""}`;
    if (delta.type === "thinking_delta")
      block.thinking = `${block.thinking ?? ""}${delta.thinking ?? ""}`;
    if (delta.type === "signature_delta") block.signature = delta.signature;
    if (delta.type === "input_json_delta")
      toolInputs.set(index, `${toolInputs.get(index) ?? ""}${delta.partial_json ?? ""}`);
  }
  let encrypted = false;
  for (const [index, block] of [...blocks.entries()].sort(([left], [right]) => left - right)) {
    if (block.type === "text") reply.text += nonEmptyString(block.text) ?? "";
    else if (block.type === "thinking") {
      const thinking = nonEmptyString(block.thinking);
      if (thinking) reply.reasoning += (reply.reasoning ? "\n\n" : "") + thinking;
      else encrypted = true;
    } else if (block.type === "redacted_thinking") encrypted = true;
    else if (block.type === "tool_use" || block.type === "server_tool_use")
      reply.toolCalls.push(toolCallOf(block, toolInputs.get(index)));
  }
  reply.reasoningEncryptedWithoutSummary = encrypted && !reply.reasoning;
  if (stopReason) {
    reply.finishReasons = [finishReason(stopReason)];
    reply.nativeFinishReasons = [stopReason];
  }
  return reply;
}

function toolCallOf(
  block: Record<string, unknown>,
  streamedInput: string | undefined,
): ModelToolCall {
  let input: unknown = block.input;
  if (streamedInput) {
    try {
      input = JSON.parse(streamedInput);
    } catch {
      input = streamedInput;
    }
  }
  return {
    id: nonEmptyString(block.id),
    name: nonEmptyString(block.name) ?? "a tool",
    arguments: input,
  };
}

function readChunks(responseBody: string): StreamChunk[] {
  const toolNames = new Map<number, string>();
  return jsonObjects(responseBody).map((event): StreamChunk => {
    const index = finiteNumber(event.index) ?? 0;
    const delta = isRecord(event.delta) ? event.delta : {};
    if (event.type === "content_block_start" && isRecord(event.content_block)) {
      const block = event.content_block;
      if (block.type !== "tool_use" && block.type !== "server_tool_use") return { kind: "other" };
      const name = nonEmptyString(block.name) ?? "a tool";
      toolNames.set(index, name);
      return { kind: "tool", tool: name, call: String(index), text: "" };
    }
    if (event.type === "content_block_delta") {
      if (delta.type === "text_delta") return { kind: "text", text: String(delta.text ?? "") };
      if (delta.type === "thinking_delta")
        return { kind: "reasoning", text: String(delta.thinking ?? "") };
      if (delta.type === "signature_delta") return { kind: "reasoning", text: "" };
      if (delta.type === "input_json_delta")
        return {
          kind: "tool",
          tool: toolNames.get(index) ?? "a tool",
          call: String(index),
          text: String(delta.partial_json ?? ""),
        };
    }
    if (event.type === "message_delta")
      return delta.stop_reason ? { kind: "finish" } : { kind: "usage" };
    return { kind: "other" };
  });
}

function finishReason(reason: string): string {
  if (reason === "tool_use") return "tool_call";
  if (reason === "max_tokens") return "length";
  if (reason === "refusal") return "content_filter";
  return "stop";
}

/**
 * What an Anthropic request asked for. Pax has no Priority Tier commitment, so every request runs
 * on the standard tier; inference runs globally unless the request keeps it in the US.
 */
function anthropicRequestedFacts(
  model: string | undefined,
  inferenceGeo: string | undefined,
): Partial<RoutingFacts> {
  return {
    ...(model === undefined ? {} : { model }),
    tier: "standard",
    inferenceLocation: inferenceGeo ?? "global",
  };
}

function anthropicSkuOf(requested: Partial<RoutingFacts>): string | undefined {
  const { model, tier, inferenceLocation: endpoint } = requested;
  if (model === undefined || tier === undefined || endpoint === undefined) return;
  return skuIdOf({ billedBy: "anthropic", model: canonicalModelName(model), endpoint, tier });
}
